import { CommonModule, formatDate } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
  ViewChild,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslateModule } from "@ngx-translate/core";
import { firstValueFrom, Observable, of, Subject, Subscription } from "rxjs";
import {
  distinctUntilChanged,
} from "rxjs/operators";
import { LabelMode } from "src/utils/label-mode.enum";
import { GenerateRandom } from "src/utils/random";
import { NgSelectComponent } from "@ng-select/ng-select";
import * as _ from "underscore";

export interface SelectBulkAction {
  label: string;
  values: unknown[];
}

export type SelectLabelSize = "default" | "compact";

type QueryParamPrimitive = string | number | boolean;
type QueryParams = Record<
  string,
  QueryParamPrimitive | readonly QueryParamPrimitive[]
>;

@Component({
  selector: "app-select",
  templateUrl: "./select.component.html",
  styleUrls: ["./select.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, NgSelectModule],
})
export class SelectComponent
  implements OnInit, OnDestroy, ControlValueAccessor, OnChanges
{
  @Input() checkboxModel = false;
  @Input() appendTo = "body";
  @Input() name = GenerateRandom(6);
  @Input() label: string;
  @Input() labelSize: SelectLabelSize = "default";
  @Input() addonLabel: string;
  @Input() addonLabelClass: string;
  @Input() placeholder = "";
  @Input() bindLabel = "name";
  @Input() optionBindLabel: string | null = null;
  @Input() bindValue = "id";
  @Input() ignoreValue: any[] = [];
  @Input() groupBy: string;
  @Input() isMultiple = false;
  @Input() showSelectAll = false;
  @Input() selectAllLabel = "เลือกทั้งหมด";
  @Input() bulkActions: SelectBulkAction[] = [];
  @Input() multilineOption = false;
  @Input() selectOptionOnPointerDown = false;

  @Input() options: unknown[] = [];
  @Input() url: string;
  @Input() limit = "50";
  @Input() disabledLimit = false;
  @Input() clearSearchOnAdd = true;

  @Input() inputClass: string;
  @Input() classes: string;
  @Input() errorMessage = "VALIDATOR.PLEASE_SELECT";
  @Input() clearable = true;
  @Input() searchable = true;
  @Input() fetchable = false;
  @Input() required = false;

  @Input() selectableGroup = false;
  @Input() selectableGroupAsModel = true;
  @Input() closeOnSelect = true;
  @Input() noneDisplayBadgeSelectGroup = false;

  @Output() keywordChange = new EventEmitter();
  @Output() objectChange = new EventEmitter();
  @Output() valueChange = new EventEmitter();
  @Output() inputChange = new EventEmitter();
  @Output() cancelChange = new EventEmitter();
  @Output() applyChange = new EventEmitter();

  @ViewChild('selectRef')
  private selectRef?: NgSelectComponent;

  get labelSizeClass(): string {
    return this.labelSize === "compact"
      ? "app-select-label--compact"
      : "fs-6";
  }

  LabelMode = LabelMode;
  _query: Record<string, unknown> = {};
  _isDisabled: boolean;
  mode: LabelMode = LabelMode.DEFAULT;
  keyword: string | null = null;

  form = new FormControl();
  subscription: Subscription;
  searchSubscription?: Subscription;

  options$: Observable<any[]> = of([]);
  search$ = new Subject<string>();
  isLoading = false;
  isError = false;

  #http = inject(HttpClient);
  #translate = inject(TranslateService);
  #cdr = inject(ChangeDetectorRef);
  // private errMsgService: ErrorMassageService,
  // private memberService: MemberService

  constructor() {}

  @Input()
  set isDisabled(value: boolean) {
    this._isDisabled = value;
    if (value) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  @Input()
  set error(value: unknown) {
    this.isError = false;
    if (value !== null && value !== undefined && value !== false) {
      this.isError = true;
    }
  }

  @Input() set labelMode(value: LabelMode) {
    this.mode = value;
  }

  @Input() set query(value: Record<string, unknown>) {
    this._query = value;
    setTimeout(() => {
      void this.getItems();
    }, 100);
  }

  onChange = (value: string) => {};
  onTouched = (value: string) => {};
  onSearchFn = (term: string, item: Record<string, string>): boolean => {
    if (!this.url) {
      const searchLabel = this.optionBindLabel ?? this.bindLabel;

      item[searchLabel] = item[searchLabel].replace(",", "");
      term = term.toLocaleLowerCase();
      return item[searchLabel].toLocaleLowerCase().includes(term);
    } else {
      return true;
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (
      Object.prototype.hasOwnProperty.call(changes, "ignoreValue") &&
      changes["ignoreValue"].currentValue !== undefined
    ) {
      void this.getItems();
    }
  }

  ngOnInit(): void {
    this.subscription = this.form.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.onChange(this.form.value);
        this.returnValueChange();
      });

    // if (this.fetchable) {
    //   this.setTypeahead();
    // }
  }

  onInputChange(value: any) {
    this.inputChange.emit(value);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  writeValue(obj: any): void {
    this.form.setValue(obj);
    void this.getItems();
  }

  setDisabledState(isDisabled: boolean): void {
    this._isDisabled = isDisabled;
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
  }

  onClearSelected() {
    if (this.isMultiple && this.selectableGroup) {
      this.options = this.url ? [] : this.options;
      this.form.setValue(null);
    }
    void this.getItems();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onSearch(term: any) {
    this.keyword = term.term;
    if (this.isMultiple && this.selectableGroup) {
      this.options = this.url ? [] : this.options;
      this.form.setValue(null);
    }
    void this.getItems();
  }

  async getItems() {
    if (this.url) {
      const params = this.setParams({
        ...this._query,
        limit: this.limit,
        keywords: this.keyword,
      });
      this.keyword = "";
      const response: Record<string, unknown>[] = await firstValueFrom(
        this.#http.get<Record<string, unknown>[]>(this.url, { params })
      );
      //end
      this.options = response
        .map((item): Record<string, unknown> => {
          const label = item[this.bindLabel];
          const translatedLabel = this.toDisplayString(label);
          if (translatedLabel !== "") {
            item[this.bindLabel] = this.#translate.instant(
              translatedLabel
            );
          }
          return item;
        })
        .filter((item) => {
          return !this.ignoreValue.includes(item[this.bindValue]);
        });

      this.returnValueChange();

      this.#cdr.detectChanges();
    }
  }

  returnValueChange() {
    if (this.isMultiple && Array.isArray(this.form.value)) {
      const map = this.form.value.map((element) => {
        return this.options.find(
          (item) => this.getOptionField(item, this.bindValue) === element
        );
      });

      if (!this.selectableGroup || this.noneDisplayBadgeSelectGroup) {
        this.valueChange.emit(map);
      }
    } else {
      const find = _.find(
        this.options,
        (item) => this.getOptionField(item, this.bindValue) === this.form.value
      );

      this.valueChange.emit(find);
    }
  }

  toggleCheckAll(value: boolean) {
    if (value) {
      this.form.patchValue(this.options.map((x) => this.getOptionValue(x)));
      this.inputChange.emit(this.options);
    } else {
      this.form.patchValue(null);
      this.inputChange.emit([]);
    }
  }

  applyBulkAction(action: SelectBulkAction) {
    const availableValues = new Set(this.options.map((option) => this.getOptionValue(option)));
    const values = action.values.filter((value) => availableValues.has(value));

    this.form.patchValue(values);
    this.inputChange.emit(this.getSelectedOptions(values));
  }

  onSingleOptionSelectPointerDown(item: unknown, event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    this.stopMultilineMouseEvent(event);

    const value = this.getOptionValue(item);
    this.form.patchValue(value);
    this.inputChange.emit(item);
    this.selectRef?.close();
  }

  onMultilineOptionTogglePointerDown(item: unknown, event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    this.stopMultilineMouseEvent(event);
    this.toggleMultilineOption(item);
  }

  onMultilineOptionToggleKeydown(item: unknown, event: KeyboardEvent): void {
    if (!this.isKeyboardActivation(event)) {
      return;
    }

    this.stopMultilineMouseEvent(event);
    this.toggleMultilineOption(item);
  }

  onMultilineValueClearPointerDown(item: unknown, event: PointerEvent): void {
    if (this._isDisabled || (event.pointerType === 'mouse' && event.button !== 0)) {
      return;
    }

    this.stopMultilineMouseEvent(event);
    this.clearMultilineValue(item);
  }

  onMultilineValueClearKeydown(item: unknown, event: KeyboardEvent): void {
    if (this._isDisabled || !this.isKeyboardActivation(event)) {
      return;
    }

    this.stopMultilineMouseEvent(event);
    this.clearMultilineValue(item);
  }

  stopMultilineMouseEvent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  stopMultilinePressEvent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onSelectClick() {}

  formatMultilineLabel(value: unknown): string[] {
    const label = this.toDisplayString(value).trim();

    if (!label) {
      return [];
    }

    return label
      .split(/,\s*\n|\n|,\s*(?=วัน)/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  getOptionLabel(item: unknown, label?: unknown): unknown {
    if (item != null && typeof item === 'object') {
      const record = item as Record<string, unknown>;
      const itemValue = this.getFirstDisplayValue(record[this.bindValue], record.value);

      return this.getFirstDisplayValue(
        record[this.bindLabel],
        record.label,
        label,
        this.findOptionLabelByValue(itemValue),
        itemValue
      );
    }

    return this.getFirstDisplayValue(label, this.findOptionLabelByValue(item), item);
  }

  setParams(object: Record<string, unknown>): QueryParams {
    const _params: QueryParams = {};
    for (const key in object) {
      const value = object[key];
      if (value != null) {
        if (value instanceof Date) {
          _params[key] = formatDate(value, "YYYY-MM-dd", "th");
        } else {
          const paramValue = this.toQueryParamValue(value);
          if (paramValue !== undefined) {
            _params[key] = paramValue;
          }
        }
      }
    }
    if (this.disabledLimit) {
      delete _params.limit;
    }

    return _params;
  }

  getDropdownOptionLabel(item: unknown): unknown {
    if (
      this.optionBindLabel != null &&
      item != null &&
      typeof item === 'object'
    ) {
      const record = item as Record<string, unknown>;

      return this.getFirstDisplayValue(
        record[this.optionBindLabel],
        this.getOptionLabel(item)
      );
    }

    return this.getOptionLabel(item);
  }

  private toggleMultilineOption(item: unknown): void {
    if (!this.isMultiple) {
      return;
    }

    const value = this.getOptionValue(item);
    const selectedValues = this.getSelectedValues();
    const nextValues = selectedValues.some((selectedValue) => this.isSameOptionValue(selectedValue, value))
      ? selectedValues.filter((selectedValue) => !this.isSameOptionValue(selectedValue, value))
      : [...selectedValues, value];

    this.form.patchValue(nextValues);
    this.inputChange.emit(this.getSelectedOptions(nextValues));
  }

  private clearMultilineValue(item: unknown): void {
    if (!this.isMultiple) {
      return;
    }

    const value = this.getOptionValue(item);
    const nextValues = this.getSelectedValues()
      .filter((selectedValue) => !this.isSameOptionValue(selectedValue, value));

    this.form.patchValue(nextValues);
    this.inputChange.emit(this.getSelectedOptions(nextValues));
  }

  private isKeyboardActivation(event: KeyboardEvent): boolean {
    return event.key === 'Enter' || event.key === ' ';
  }

  private toQueryParamValue(
    value: unknown
  ): QueryParamPrimitive | readonly QueryParamPrimitive[] | undefined {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return value;
    }

    if (Array.isArray(value)) {
      const values = value.filter(
        (item): item is QueryParamPrimitive =>
          typeof item === "string" ||
          typeof item === "number" ||
          typeof item === "boolean"
      );

      return values.length > 0 ? values : undefined;
    }

    return undefined;
  }
  private findOptionLabelByValue(value: unknown): unknown {
    if (value == null || value === '') {
      return '';
    }

    const option = this.options.find(
      (optionItem) => this.getOptionField(optionItem, this.bindValue) === value
    );

    return this.getOptionField(option, this.bindLabel) ?? '';
  }

  private getFirstDisplayValue(...values: unknown[]): unknown {
    return values.find((value) => this.hasDisplayValue(value)) ?? '';
  }

  private hasDisplayValue(value: unknown): boolean {
    if (value == null) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return typeof value === 'number' || typeof value === 'boolean';
  }

  private getOptionValue(item: unknown): unknown {
    if (item != null && typeof item === 'object') {
      const record = item as Record<string, unknown>;
      return this.getFirstDisplayValue(record[this.bindValue], record.value, item);
    }

    return item;
  }

  private getSelectedValues(): unknown[] {
    return Array.isArray(this.form.value) ? this.form.value : [];
  }

  private getSelectedOptions(values: unknown[]): unknown[] {
    return values
      .map((value) =>
        this.options.find((item) =>
          this.isSameOptionValue(this.getOptionField(item, this.bindValue), value)
        )
      )
      .filter((item: unknown) => item != null);
  }

  private isSameOptionValue(first: unknown, second: unknown): boolean {
    return this.toDisplayString(first) === this.toDisplayString(second);
  }

  private getOptionField(item: unknown, key: string): unknown {
    if (item != null && typeof item === "object") {
      return (item as Record<string, unknown>)[key];
    }

    return undefined;
  }

  private toDisplayString(value: unknown): string {
    if (value === null || value === undefined) {
      return "";
    }

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return String(value);
    }

    return "";
  }

}
